<script lang="ts" setup>
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SubscriptionsTabContent from "./components/SubscriptionsTabContent.vue";
import ContactTabContent from "./components/ContactTabContent.vue";

import { computed, ref, watch } from "vue";
import { useCurrentOrg, useUpdateOrg } from "@/queries/orgs";
import { useRouter } from "vue-router";
import { useSessionStore } from "@/stores/session-store";

const router = useRouter();
const sessionStore = useSessionStore();
const { org } = useCurrentOrg();
const { updateOrgAsync, updateOrgPending } = useUpdateOrg();

const canEdit = computed(() =>
  sessionStore.isSuperAdmin || sessionStore.role === "OrgAdmin"
);

const orgName = ref("");
watch(org, (o) => { if (o) orgName.value = o.orgName; }, { immediate: true });

const isValidOrgName = computed(() => orgName.value.trim().length >= 3);
const hasChanges = computed(() => !!org.value && orgName.value !== org.value.orgName);
const canSave = computed(() =>
  canEdit.value && hasChanges.value && isValidOrgName.value && !updateOrgPending.value
);

const successMessage = ref("");
const errorMessage = ref("");

async function handleSave() {
  if (!canSave.value) return;
  errorMessage.value = "";
  successMessage.value = "";
  try {
    await updateOrgAsync({ orgName: orgName.value.trim() });
    await sessionStore.syncSession();
    successMessage.value = "Organisation updated successfully";
    setTimeout(() => { successMessage.value = ""; }, 3000);
  } catch (err) {
    errorMessage.value = err instanceof Error ? err.message : "Failed to update organisation";
  }
}

function handleCancel() {
  router.back();
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Organisation Settings"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Organisation', href: '/org' },
          { label: 'Settings' }
        ]"
      />
      <ul class="nav nav-tabs" id="myTab" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link active"
            id="general-tab"
            data-bs-toggle="tab"
            data-bs-target="#general-tab-pane"
            type="button"
            role="tab"
            aria-controls="general-tab-pane"
            aria-selected="true"
          >
            General
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            id="subscription-tab"
            data-bs-toggle="tab"
            data-bs-target="#subscription-tab-pane"
            type="button"
            role="tab"
            aria-controls="subscription-tab-pane"
            aria-selected="false"
          >
            Subscription
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            id="contact-tab"
            data-bs-toggle="tab"
            data-bs-target="#contact-tab-pane"
            type="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected="false"
          >
            Contact
          </button>
        </li>
      </ul>

      <div class="tab-content" id="myTabContent">
        <div
          class="tab-pane p-3 fade show active"
          id="general-tab-pane"
          role="tabpanel"
          aria-labelledby="general-tab"
          tabindex="0"
        >
          <form @submit.prevent="handleSave">
            <div v-if="successMessage" class="alert alert-success" role="alert">
              <i class="bi bi-check-circle me-2"></i>{{ successMessage }}
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
            </div>
            <div class="row mb-3">
              <label for="name" class="col-sm-3 col-form-label">Name</label>
              <div class="col-sm-9">
                <template v-if="canEdit">
                  <input
                    id="name"
                    type="text"
                    v-model="orgName"
                    class="form-control"
                    :class="{ 'is-invalid': orgName.length > 0 && !isValidOrgName }"
                    :disabled="updateOrgPending"
                  />
                  <div v-if="orgName.length > 0 && !isValidOrgName" class="invalid-feedback">
                    Name must be at least 3 characters
                  </div>
                </template>
                <div v-else class="form-control-plaintext">{{ orgName }}</div>
              </div>
            </div>

            <div v-if="canEdit" class="d-flex justify-content-end gap-2">
              <button type="button" class="bb-btn-cancel" @click="handleCancel" :disabled="updateOrgPending">Cancel</button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="!canSave">
                <span v-if="updateOrgPending" class="spinner-border spinner-border-sm me-2"></span>
                {{ updateOrgPending ? "Saving..." : "Save Changes" }}
              </button>
            </div>
          </form>
        </div>
        <div
          class="tab-pane p-3 fade"
          id="subscription-tab-pane"
          role="tabpanel"
          aria-labelledby="subscription-tab"
          tabindex="0"
        >
          <SubscriptionsTabContent />
        </div>
        <div
          class="tab-pane p-3 fade"
          id="contact-tab-pane"
          role="tabpanel"
          aria-labelledby="contact-tab"
          tabindex="0"
        >
          <ContactTabContent />
        </div>
        <div
          class="tab-pane fade"
          id="disabled-tab-pane"
          role="tabpanel"
          aria-labelledby="disabled-tab"
          tabindex="0"
        >
          ...
        </div>
      </div>
    </PanelCard>
  </PanelGrid>
</template>
